import { useEffect, useState } from "react";
import { ChevronLeft, Truck, Package, Loader2, ExternalLink, Printer, FileText } from "lucide-react";
import { formatCurrency } from "@/config/locale";
import { Button } from "@/app/components/button";
import {
  getOrder,
  getAccessToken,
  getBffBaseUrl,
  cancelOrder,
  confirmOrderReceipt,
  buildTrackingUrl,
  postAuthorizeDCEShipment,
  postCancelDCEShipment,
  postRequestShipmentLabelGeneration,
  LOCAL_HANDOFF_SHIPPING_OPTION_ID,
  type OrderDetailApi,
  type OrderShipmentApi,
} from "@/app/api/auth-client";

interface OrderDetailPageProps {
  orderId: string;
  onBack: () => void;
  /** When true, user opened the order from Sales (seller); from Purchases (buyer) when false. Swap: each party sees their legs + actions; the other party's leg shows read-only envio/DC-e info. */
  isSellerView?: boolean;
  /** When provided and order status is pending_payment, show a button to go to payment (buyer or seller as second payer). */
  onPayOrder?: (orderId: string) => void | Promise<void>;
  /** When provided and order is swap with current user as second payer, navigate to second-party checkout instead of direct Stripe. */
  onStartSecondPartyCheckout?: (orderId: string) => void;
}

/** Order status display labels (payment/order state). */
const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  pending_payment: "Pagamento pendente",
  awaiting_second_payment: "Aguardando pagamento da outra parte", // swap: current user already paid
  shipping_pending: "Aguardando envio",
  in_delivery: "Em entrega",
  delivered: "Entregue",
  paid: "Pago", // backward compat
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};

/** Buyer: unpaid abandon + paid awaiting shipment (canonical `shipping_pending` after payment). */
const BUYER_ORDER_CANCEL_STATUSES = ["pending_payment", "shipping_pending"];
/** Seller: only while awaiting shipment (same post-payment state as buyer after Stripe). */
const SELLER_ORDER_CANCEL_STATUSES = ["shipping_pending"];

/** Poll order detail while carrier label is not yet ready (matches `label_status === "ready"` or resolved label URL). */
const ORDER_LABEL_POLL_INTERVAL_MS = 120_000;

const SHIPMENT_STATUS_LABELS: Record<string, string> = {
  label_issued: "Pronto para Envio",
  posted: "Postado",
  in_transit: "Em trânsito",
  out_for_delivery: "Saiu para entrega",
  delivered: "Entregue",
  cancelled: "Cancelado",
};

const DCE_STATUS_LABELS: Record<string, string> = {
  not_requested: "Não solicitado",
  draft: "Rascunho",
  authorizing: "Autorizando",
  authorized: "Autorizado",
  rejected: "Rejeitado",
  cancelled: "Cancelado",
  canceled: "Cancelado",
  cancel_pending: "Cancelamento em andamento",
};

function formatDCEStatus(status: string | undefined): string {
  const k = (status ?? "").trim().toLowerCase();
  if (!k) return DCE_STATUS_LABELS.not_requested;
  return DCE_STATUS_LABELS[k] ?? status!.replace(/_/g, " ");
}

/** Same status→color mapping as purchases-page for consistency. */
function orderStatusBadgeClass(status: string): string {
  switch (status) {
    case "pending_payment":
    case "awaiting_second_payment":
      return "text-orange-600 bg-orange-50 dark:bg-orange-950/30";
    case "paid":
    case "shipping_pending":
      return "text-amber-600 bg-amber-50 dark:bg-amber-950/30";
    case "in_delivery":
      return "text-blue-600 bg-blue-50 dark:bg-blue-950/30";
    case "delivered":
      return "text-green-600 bg-green-50 dark:bg-green-950/30";
    case "cancelled":
      return "text-red-600 bg-red-50 dark:bg-red-950/30";
    default:
      return "text-gray-600 bg-gray-50 dark:bg-gray-950/30";
  }
}

/** Shared pill-style badge for order and shipment status. Order status uses status-specific colors; shipment uses primary. */
function StatusBadge({ label, status }: { label: string; status?: string }) {
  const className =
    status != null
      ? orderStatusBadgeClass(status)
      : "bg-primary/10 text-primary";
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${className}`}>
      {label}
    </span>
  );
}

function formatOrderStatus(status: string): string {
  return ORDER_STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}

function formatShipmentStatus(status: string): string {
  return SHIPMENT_STATUS_LABELS[status] ?? status;
}

/** Carrier IDs from APIs are often lowercase; show Title Case per word (Correios, Melhor Envio). */
function formatCarrierDisplayName(carrier: string | undefined): string {
  const t = (carrier ?? "").trim();
  if (!t) return "Transportadora";
  return t
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .filter(Boolean)
    .join(" ");
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Safe segment for PDF filename; matches BFF shipmentLabelDownloadFilename sanitization. */
function sanitizeLabelDownloadFilenameSegment(s: string): string {
  const t = s
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^[._]+|[._]+$/g, "");
  return t || "pedido";
}

/** Stable name: "SM-<YYYYMMDD>-<order>-<n>.pdf" (matches BFF Content-Disposition; n = 1-based shipment index). */
function buildShipmentLabelDownloadFilename(orderId: string, shipmentIndex1based: number): string {
  const n = shipmentIndex1based >= 1 ? shipmentIndex1based : 1;
  let base = sanitizeLabelDownloadFilenameSegment(orderId);
  if (base.length > 48) base = base.slice(-48);
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return `SM-${date}-${base}-${n}.pdf`;
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;\s]+)|filename\*=([^;\s]+)/i.exec(header);
  if (star?.[1] ?? star?.[2]) {
    try {
      return decodeURIComponent((star[1] ?? star[2] ?? "").replace(/\+/g, " "));
    } catch {
      return star[1] ?? star[2] ?? null;
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain?.[1] ?? null;
}

async function openLabelURL(
  url: string,
  opts?: { fallbackFilename?: string; kind?: "label" | "dace" }
): Promise<void> {
  const u = (url ?? "").trim();
  if (!u) return;
  const internalPath = (() => {
    if (u.startsWith("/auth/me/")) return u;
    try {
      const parsed = new URL(u, window.location.origin);
      if (parsed.origin === window.location.origin && parsed.pathname.startsWith("/auth/me/")) {
        return `${parsed.pathname}${parsed.search}`;
      }
    } catch {
      // Keep fallback path handling below.
    }
    return "";
  })();
  if (internalPath) {
    const token = getAccessToken();
    const bffURL = `${getBffBaseUrl()}${internalPath}`;
    const resp = await fetch(bffURL, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (resp.status === 401) throw new Error("Sua sessão expirou. Faça login novamente.");
    const isDace = opts?.kind === "dace";
    if (!resp.ok) {
      throw new Error(isDace ? "Não foi possível abrir o DACE." : "Não foi possível abrir a etiqueta.");
    }
    const ct = (resp.headers.get("content-type") ?? "").toLowerCase();
    if (!ct.includes("application/pdf")) {
      const body = (await resp.text()).trim();
      if (body) {
        throw new Error(
          `${isDace ? "DACE" : "Etiqueta"} indisponível no momento (${body.slice(0, 180)})`
        );
      }
      throw new Error(isDace ? "DACE indisponível no momento." : "Etiqueta indisponível no momento.");
    }
    const blob = await resp.blob();
    if ((blob.size ?? 0) === 0) {
      throw new Error(isDace ? "DACE vazio ou indisponível no momento." : "Etiqueta vazia ou indisponível no momento.");
    }
    const headerName = parseFilenameFromContentDisposition(resp.headers.get("content-disposition"));
    const filename =
      (headerName && headerName.trim()) ||
      opts?.fallbackFilename?.trim() ||
      (isDace ? "dace.pdf" : "etiqueta.pdf");
    const named = new File([blob], filename, { type: "application/pdf" });
    const blobURL = URL.createObjectURL(named);
    window.open(blobURL, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobURL), 60_000);
    return;
  }
  // Guard against malformed same-origin paths (for example "/<uuid>"), which open blank pages.
  if (u.startsWith("/")) {
    throw new Error("Link da etiqueta inválido. Tente atualizar a página.");
  }
  window.open(u, "_blank", "noopener,noreferrer");
}

/** Hosts we treat as our app origin for label/DACE URL normalization (legacy + current domains). */
function isInternalAppHost(hostname: string): boolean {
  const host = (hostname ?? "").toLowerCase().trim();
  if (!host) return false;
  const current = (window.location.hostname ?? "").toLowerCase().trim();
  if (host === current) return true;
  const known = new Set([
    "ezyhub.com.br",
    "www.ezyhub.com.br",
    "akihub.com.br",
    "www.akihub.com.br",
    "akihub.io",
    "www.akihub.io",
  ]);
  return known.has(host);
}

function resolveShipmentLabelURL(orderId: string, shipment: OrderShipmentApi): string {
  const proxyURL =
    shipment.label_storage_object && shipment.shipment_id
      ? `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipment.shipment_id)}/label`
      : "";
  const raw = (shipment.label_url ?? "").trim();
  if (!raw) return proxyURL;
  if (raw.startsWith("/auth/me/")) return raw;
  if (raw.startsWith("data:application/pdf")) {
    return raw;
  }
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const parsed = new URL(raw);
      // Any internal app URL must use authenticated BFF path.
      if (isInternalAppHost(parsed.hostname)) {
        if (parsed.pathname.startsWith("/auth/me/")) {
          return `${parsed.pathname}${parsed.search}`;
        }
        if (proxyURL) return proxyURL;
        return "";
      }
    } catch {
      // Keep fallback behavior below.
    }
    return raw;
  }
  // Any other relative/suspicious value falls back to authenticated proxy when available.
  if (proxyURL) return proxyURL;
  return "";
}

/** Stable name for DACE PDF fallback (BFF may send filename via Content-Disposition). */
function buildDaceDownloadFilename(orderId: string, shipmentIndex1based: number): string {
  const n = shipmentIndex1based >= 1 ? shipmentIndex1based : 1;
  return `dace-${sanitizeLabelDownloadFilenameSegment(orderId)}-${n}.pdf`;
}

function buildDaceCancelledDownloadFilename(orderId: string, shipmentIndex1based: number): string {
  const n = shipmentIndex1based >= 1 ? shipmentIndex1based : 1;
  return `dace-${sanitizeLabelDownloadFilenameSegment(orderId)}-${n}-cancelado.pdf`;
}

function resolveShipmentDaceURL(orderId: string, shipment: OrderShipmentApi): string {
  const sid = (shipment.shipment_id ?? "").trim();
  const proxyURL = sid
    ? `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(sid)}/dace`
    : "";
  const raw = (shipment.dce_dace_url ?? "").trim();
  if (!raw) {
    return shipment.dce_dace_storage_object ? proxyURL : "";
  }
  if (raw.startsWith("/auth/me/")) return raw;
  if (raw.startsWith("data:application/pdf")) return raw;
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const parsed = new URL(raw);
      if (isInternalAppHost(parsed.hostname)) {
        if (parsed.pathname.startsWith("/auth/me/")) {
          return `${parsed.pathname}${parsed.search}`;
        }
        if (proxyURL && (shipment.dce_dace_storage_object || shipment.dce_dace_url)) return proxyURL;
        return "";
      }
    } catch {
      // Keep fallback behavior below.
    }
    return raw;
  }
  if (proxyURL && shipment.dce_dace_storage_object) return proxyURL;
  return "";
}

function resolveShipmentDaceCancelledURL(orderId: string, shipment: OrderShipmentApi): string {
  const sid = (shipment.shipment_id ?? "").trim();
  const proxyURL = sid
    ? `/auth/me/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(sid)}/dace?variant=cancelled`
    : "";
  const raw = (shipment.dce_dace_cancelled_url ?? "").trim();
  if (!raw) {
    return shipment.dce_cancelled_dace_storage_object ? proxyURL : "";
  }
  if (raw.startsWith("/auth/me/")) {
    if (raw.includes("variant=cancelled")) return raw;
    const sep = raw.includes("?") ? "&" : "?";
    return `${raw}${sep}variant=cancelled`;
  }
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const parsed = new URL(raw);
      if (isInternalAppHost(parsed.hostname) && parsed.pathname.startsWith("/auth/me/")) {
        return `${parsed.pathname}${parsed.search || "?variant=cancelled"}`;
      }
    } catch {
      /* ignore */
    }
    return raw;
  }
  if (proxyURL && shipment.dce_cancelled_dace_storage_object) return proxyURL;
  return "";
}

function hasShipmentDCEInfo(shipment: OrderShipmentApi): boolean {
  const t = (s: string | undefined) => (s ?? "").trim();
  return !!(
    t(shipment.dce_status) ||
    t(shipment.dce_chave_acesso) ||
    t(shipment.dce_protocolo) ||
    t(shipment.dce_dace_url) ||
    t(shipment.dce_dace_storage_object) ||
    t(shipment.dce_last_cstat) ||
    t(shipment.dce_last_xmotivo)
  );
}

function hasCancelledDCESnapshot(shipment: OrderShipmentApi): boolean {
  const t = (s: string | undefined) => (s ?? "").trim();
  return !!(
    t(shipment.dce_cancelled_chave_acesso) ||
    t(shipment.dce_cancelled_dace_storage_object) ||
    t(shipment.dce_dace_cancelled_url)
  );
}

/** Swap: leg 1 and 2.1 = seller's label; leg 1.1 and 2 = buyer's label. Non-swap: single shipment = seller. */
function shipmentBelongsToCurrentUser(shipment: OrderShipmentApi, isSellerView: boolean, dealType?: string): boolean {
  const leg = (shipment.leg ?? "").trim();
  if ((dealType ?? "").trim().toLowerCase() === "swap") {
    if (leg === "1" || leg === "2.1") return isSellerView;
    if (leg === "1.1" || leg === "2") return !isSellerView;
  }
  return isSellerView;
}

/**
 * True when some shipment visible to the viewer (same rules as "Ver e imprimir etiqueta") still has no generated label:
 * not `label_status === "ready"` and no resolvable label URL yet. Stops polling for cancelled/refunded orders.
 */
function orderViewerWaitingForCarrierLabel(order: OrderDetailApi, orderId: string, isSellerView: boolean): boolean {
  const os = (order.status ?? "").trim().toLowerCase();
  if (os === "cancelled" || os === "refunded") return false;
  const dealType = order.deal_type;
  for (const s of order.shipments ?? []) {
    const showLabelLink =
      shipmentBelongsToCurrentUser(s, isSellerView, dealType) && (s.carrier ?? "").toLowerCase() !== "ezyhub";
    if (!showLabelLink) continue;
    const ls = (s.label_status ?? "").trim().toLowerCase();
    const hasLabelUrl = !!resolveShipmentLabelURL(orderId, s).trim();
    if (ls === "ready" || hasLabelUrl) continue;
    return true;
  }
  return false;
}

/** Swap: human-readable label for who this shipment belongs to (for UI subtitle). Current user's shipment = "Sua remessa"; other party's = "Remessa do comprador" or "Remessa do vendedor". */
function shipmentPartyLabel(shipment: OrderShipmentApi, dealType: string | undefined, isSellerView: boolean): string {
  if ((dealType ?? "").trim().toLowerCase() !== "swap") return "";
  const belongsToCurrentUser = shipmentBelongsToCurrentUser(shipment, isSellerView, dealType);
  if (belongsToCurrentUser) return "Sua remessa";
  return isSellerView ? "Remessa do comprador" : "Remessa do vendedor";
}

/** Swap order: current user already paid their part (first payer) => show "awaiting other party" and hide pay CTA. */
function isSwapFirstPayerPaid(order: OrderDetailApi, isSellerView: boolean): boolean {
  if (order.deal_type !== "swap" || !order.payment_intent_id) return false;
  const firstPayer = (order.first_payer_party || "").trim().toLowerCase();
  if (isSellerView) return firstPayer === "seller";
  return firstPayer === "buyer";
}

/** Freight only (Melhor Envio quote + markup). Do not use (total - subtotal): that mixes in platform_fee_cents. */
function orderFreightCents(order: OrderDetailApi): number {
  const pf = order.platform_fee_cents ?? 0;
  const sh = order.shipping_cents ?? 0;
  if (sh > 0 || pf === 0) return Math.max(0, sh);
  // Legacy orders: total may omit separate shipping field; split delta by known platform fee
  const delta = order.total_cents - order.subtotal_cents;
  return Math.max(0, delta - pf);
}

function orderPlatformFeeCents(order: OrderDetailApi): number {
  return Math.max(0, order.platform_fee_cents ?? 0);
}

/** True when swap and both leg 1 and leg 1.1 have status "delivered" (products received at hub). */
function bothLegs1And11DeliveredAtHub(shipments: OrderShipmentApi[]): boolean {
  const leg1 = shipments.find((s) => (s.leg ?? "").trim() === "1");
  const leg11 = shipments.find((s) => (s.leg ?? "").trim() === "1.1");
  const delivered = (s: OrderShipmentApi) => (s.status ?? "").toLowerCase() === "delivered";
  return !!leg1 && !!leg11 && delivered(leg1) && delivered(leg11);
}

export function OrderDetailPage({ orderId, onBack, isSellerView = false, onPayOrder, onStartSecondPartyCheckout }: OrderDetailPageProps) {
  const [order, setOrder] = useState<OrderDetailApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [confirmReceiptLoading, setConfirmReceiptLoading] = useState(false);
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);

  const isFirstPayerPaid = order ? isSwapFirstPayerPaid(order, isSellerView) : false;
  const displayOrderStatus = order?.status === "pending_payment" && isFirstPayerPaid ? "awaiting_second_payment" : (order?.status ?? "");
  const isSwapSecondPayer = !!(order?.deal_type === "swap" && order?.status === "pending_payment" && !isFirstPayerPaid);
  const isLocalHandoffOrder = order?.shipping_option_id === LOCAL_HANDOFF_SHIPPING_OPTION_ID;
  const localHandoffPostPayStatuses = ["shipping_pending", "in_delivery", "delivered"];
  const showLocalHandoffDualCta = !!(
    isLocalHandoffOrder && order && localHandoffPostPayStatuses.includes(order.status)
  );
  /** Retirada presencial: cancelar antes de qualquer confirmação de entrega/recebimento (escrow). */
  const handoffNeitherPartyConfirmed =
    !!order &&
    isLocalHandoffOrder &&
    order.status === "in_delivery" &&
    order.escrow_confirmed_buyer !== true &&
    order.escrow_confirmed_seller !== true;
  const showPayOrderButton = !!(
    order?.status === "pending_payment" &&
    !isFirstPayerPaid &&
    (onPayOrder || onStartSecondPartyCheckout)
  );

  useEffect(() => {
    let cancelled = false;
    getOrder(orderId)
      .then((data) => {
        if (!cancelled) setOrder(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? "Não foi possível carregar o pedido.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const needsLabelStatusPoll = order ? orderViewerWaitingForCarrierLabel(order, orderId, isSellerView) : false;
  useEffect(() => {
    if (!needsLabelStatusPoll) return;
    const id = window.setInterval(() => {
      void getOrder(orderId)
        .then((data) => setOrder(data))
        .catch(() => {
          /* keep last good order; user can refresh manually */
        });
    }, ORDER_LABEL_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [orderId, isSellerView, needsLabelStatusPoll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary border-b border-primary sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl text-white">Detalhe do Pedido</h1>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">{error ?? "Pedido não encontrado."}</p>
          <Button onClick={onBack} className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const shipments = order.shipments ?? [];
  const shipmentLabelIndexById = new Map(shipments.map((s, i) => [s.shipment_id, i + 1] as const));

  const cancelOrderConfirmMessage = handoffNeitherPartyConfirmed
    ? "Retirada presencial: ninguém confirmou ainda entrega nem recebimento. Se cancelar, o pedido será encerrado e o valor pago será reembolsado (o prazo depende do cartão ou do banco). Deseja continuar?"
    : order.status === "shipping_pending"
      ? "Tem certeza que deseja cancelar este pedido? O valor pago será reembolsado conforme o meio de pagamento."
      : "Tem certeza que deseja cancelar este pedido?";

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl text-white">Pedido #{order.order_id}</h1>
              <p className="text-sm text-white/80">
                {new Date(order.created_at).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Order status */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status do pedido</span>
            <StatusBadge label={formatOrderStatus(displayOrderStatus)} status={displayOrderStatus} />
          </div>
          {order.deal_type === "swap" && bothLegs1And11DeliveredAtHub(shipments) && (
            <div className="mt-2">
              <StatusBadge label="Produto recebido em nosso Hub!" status="delivered" />
            </div>
          )}
          <div className="mt-3 space-y-1 text-sm">
            {isSwapSecondPayer && (order.second_party_shipping_cents ?? 0) > 0 ? (
              <div className="flex justify-between text-muted-foreground">
                <span>Valor dos produtos (pago pelo comprador)</span>
                <span>{formatCurrency(order.subtotal_cents / 100)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-muted-foreground">
                  <span>Valor dos Produtos</span>
                  <span>{formatCurrency(order.subtotal_cents / 100)}</span>
                </div>
                {!isSellerView && (
                  <>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Frete</span>
                      <span>{formatCurrency(orderFreightCents(order) / 100)}</span>
                    </div>
                    {orderPlatformFeeCents(order) > 0 ? (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Taxa da plataforma</span>
                        <span>{formatCurrency(orderPlatformFeeCents(order) / 100)}</span>
                      </div>
                    ) : null}
                    {(order.buyer_handling_cents ?? 0) > 0 ? (
                      <div className="flex justify-between text-muted-foreground">
                        <span>
                          {order.deal_type === "swap"
                            ? "Custos de manuseio (hub)"
                            : "Custos de manuseio"}
                        </span>
                        <span>{formatCurrency((order.buyer_handling_cents ?? 0) / 100)}</span>
                      </div>
                    ) : null}
                  </>
                )}
                <p className="text-lg font-semibold text-primary pt-2 border-t border-border text-right">
                  {formatCurrency((isSellerView ? order.subtotal_cents : order.total_cents) / 100)}
                </p>
              </>
            )}
          </div>
          {showPayOrderButton && (
            <Button
              className="w-full mt-4"
              onClick={async () => {
                setPayLoading(true);
                try {
                  if (isSwapSecondPayer && onStartSecondPartyCheckout) {
                    onStartSecondPartyCheckout(orderId);
                  } else if (onPayOrder) {
                    await onPayOrder(orderId);
                  }
                } catch (e) {
                  const msg = (e as Error)?.message ?? "Não foi possível abrir o pagamento.";
                  alert(msg);
                } finally {
                  setPayLoading(false);
                }
              }}
              disabled={payLoading}
            >
              {payLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2 inline" />
                  {isSwapSecondPayer ? "Carregando..." : "Abrindo pagamento..."}
                </>
              ) : isSwapSecondPayer ? (
                "Opções de envio para seu produto"
              ) : (
                "Ir para pagamento"
              )}
            </Button>
          )}
          {showLocalHandoffDualCta && (
            <p className="text-sm text-muted-foreground mt-3 border border-border rounded-lg p-3 bg-muted/30">
              <strong>Retirada presencial:</strong> para liberar o valor em garantia para o vendedor,{" "}
              <strong>ambos</strong> precisam confirmar — o vendedor que entregou o produto e o comprador que
              recebeu.
            </p>
          )}
          {showLocalHandoffDualCta && isSellerView && order.escrow_confirmed_seller !== true && (
            <Button
              className="w-full mt-3"
              onClick={async () => {
                setConfirmReceiptLoading(true);
                try {
                  await confirmOrderReceipt(orderId, { party: "seller" });
                  const updated = await getOrder(orderId);
                  setOrder(updated);
                } catch (e) {
                  const msg = (e as Error)?.message ?? "Não foi possível confirmar.";
                  alert(msg);
                } finally {
                  setConfirmReceiptLoading(false);
                }
              }}
              disabled={confirmReceiptLoading}
            >
              {confirmReceiptLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2 inline" />
                  Confirmando...
                </>
              ) : (
                "Confirmar entrega do produto"
              )}
            </Button>
          )}
          {showLocalHandoffDualCta && !isSellerView && order.escrow_confirmed_buyer !== true && (
            <Button
              className="w-full mt-3"
              onClick={async () => {
                setConfirmReceiptLoading(true);
                try {
                  await confirmOrderReceipt(orderId, { party: "buyer" });
                  const updated = await getOrder(orderId);
                  setOrder(updated);
                } catch (e) {
                  const msg = (e as Error)?.message ?? "Não foi possível confirmar recebimento.";
                  alert(msg);
                } finally {
                  setConfirmReceiptLoading(false);
                }
              }}
              disabled={confirmReceiptLoading}
            >
              {confirmReceiptLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2 inline" />
                  Confirmando...
                </>
              ) : (
                "Confirmar recebimento do produto"
              )}
            </Button>
          )}
          {order.status === "delivered" &&
            !isSellerView &&
            !receiptConfirmed &&
            !isLocalHandoffOrder && (
            <Button
              className="w-full mt-3"
              onClick={async () => {
                setConfirmReceiptLoading(true);
                try {
                  await confirmOrderReceipt(orderId);
                  setReceiptConfirmed(true);
                  const updated = await getOrder(orderId);
                  setOrder(updated);
                } catch (e) {
                  const msg = (e as Error)?.message ?? "Não foi possível confirmar recebimento.";
                  alert(msg);
                } finally {
                  setConfirmReceiptLoading(false);
                }
              }}
              disabled={confirmReceiptLoading}
            >
              {confirmReceiptLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2 inline" />
                  Confirmando...
                </>
              ) : (
                "Confirmar recebimento"
              )}
            </Button>
          )}
          {((!isSellerView &&
            (BUYER_ORDER_CANCEL_STATUSES.includes(order.status) || handoffNeitherPartyConfirmed)) ||
            (isSellerView &&
              (SELLER_ORDER_CANCEL_STATUSES.includes(order.status) || handoffNeitherPartyConfirmed))) && (
            <Button
              variant="outline"
              className="w-full mt-3 border-destructive text-destructive hover:bg-destructive/10"
              onClick={async () => {
                if (!confirm(cancelOrderConfirmMessage)) return;
                setCancelLoading(true);
                try {
                  await cancelOrder(orderId);
                  const updated = await getOrder(orderId);
                  setOrder(updated);
                } catch (e) {
                  try {
                    const refreshed = await getOrder(orderId);
                    const st = (refreshed.status ?? "").toLowerCase();
                    if (st === "cancelled" || st === "refunded") {
                      setOrder(refreshed);
                      return;
                    }
                  } catch {
                    /* keep generic error below */
                  }
                  const msg = (e as Error)?.message ?? "Não foi possível cancelar o pedido.";
                  alert(msg);
                } finally {
                  setCancelLoading(false);
                }
              }}
              disabled={cancelLoading}
            >
              {cancelLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2 inline" />
                  Cancelando...
                </>
              ) : (
                "Cancelar pedido"
              )}
            </Button>
          )}
        </div>

        {/* Shipments: Rastrear + status + timeline */}
        {shipments.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              Envios
            </h2>
            {[...shipments]
              .sort((a, b) => {
                const dealType = order.deal_type ?? "";
                const aMine = shipmentBelongsToCurrentUser(a, isSellerView, dealType) ? 0 : 1;
                const bMine = shipmentBelongsToCurrentUser(b, isSellerView, dealType) ? 0 : 1;
                if (aMine !== bMine) return aMine - bMine;
                const legOrder = (leg: string) => ({ "1": 1, "1.1": 2, "2": 3, "2.1": 4 }[leg.trim()] ?? 99);
                return legOrder(a.leg ?? "") - legOrder(b.leg ?? "");
              })
              .map((shipment: OrderShipmentApi) => (
                <ShipmentCard
                  key={shipment.shipment_id}
                  orderId={order.order_id}
                  orderStatus={order.status}
                  orderUpdatedAt={order.updated_at}
                  shipmentLabelIndex1based={shipmentLabelIndexById.get(shipment.shipment_id) ?? 1}
                  shipment={shipment}
                  showLabelLink={
                    shipmentBelongsToCurrentUser(shipment, isSellerView, order.deal_type) &&
                    (shipment.carrier ?? "").toLowerCase() !== "ezyhub"
                  }
                  partyLabel={shipmentPartyLabel(shipment, order.deal_type, isSellerView)}
                  isCurrentUserShipment={shipmentBelongsToCurrentUser(shipment, isSellerView, order.deal_type)}
                  onRefreshOrder={async () => {
                    const updated = await getOrder(orderId);
                    setOrder(updated);
                  }}
                />
              ))}
          </div>
        )}

        {shipments.length === 0 &&
          ["paid", "shipping_pending", "in_delivery"].includes(order.status) && (
          <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
            <Package className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {order.shipping_option_id === LOCAL_HANDOFF_SHIPPING_OPTION_ID
                ? "Retirada presencial: após a confirmação do pagamento, combine com a outra parte data e local. As informações de envio aparecerão aqui quando o post for registrado."
                : order.deal_type === "swap"
                  ? "Os envios da troca (cada perna) aparecem aqui assim que forem criados no sistema. Se o status estiver “Aguardando envio” e nada surgir, pode haver pendência nos dados (ex.: telefone para a transportadora) ou a geração ainda não foi solicitada — atualize a página em alguns minutos ou fale com o suporte."
                  : "Seu pedido está sendo preparado. As informações de rastreio e etiqueta aparecerão aqui quando o envio for registrado. Se isso não ocorrer, verifique os dados de contato do endereço ou tente novamente mais tarde."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ShipmentCard({
  orderId,
  orderStatus,
  orderUpdatedAt,
  shipmentLabelIndex1based,
  shipment,
  showLabelLink,
  partyLabel,
  isCurrentUserShipment,
  onRefreshOrder,
}: {
  orderId: string;
  /** When cancelled/refunded, shipment UI aligns with order (badge + no tracking link). */
  orderStatus?: string;
  /** Used for cancelled-order history line (order.updated_at). */
  orderUpdatedAt?: string;
  /** 1-based index of this shipment in `order.shipments` (API order). */
  shipmentLabelIndex1based: number;
  shipment: OrderShipmentApi;
  showLabelLink: boolean;
  partyLabel?: string;
  isCurrentUserShipment?: boolean;
  onRefreshOrder?: () => Promise<void>;
}) {
  const [dceAuthLoading, setDceAuthLoading] = useState(false);
  const [dceCancelLoading, setDceCancelLoading] = useState(false);
  const [labelRequestLoading, setLabelRequestLoading] = useState(false);
  const orderClosed = (orderStatus ?? "").trim().toLowerCase() === "cancelled" || (orderStatus ?? "").trim().toLowerCase() === "refunded";
  const shipmentCancelled = (shipment.status ?? "").trim().toLowerCase() === "cancelled";
  /** No imprimir etiqueta após cancelamento do pedido ou da remessa (etiqueta inválida). */
  const hideCarrierLabelActions = orderClosed || shipmentCancelled;
  const isLocalHandoff = (shipment.carrier ?? "").toLowerCase() === "ezyhub";
  const trackingUrl =
    orderClosed || isLocalHandoff ? null : buildTrackingUrl(shipment.carrier, shipment.tracking_code);
  const displayShipmentStatus = orderClosed ? "cancelled" : shipment.status;
  const history = shipment.status_history ?? [];
  const sortedHistory = [...history].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
  );
  const shipmentClosedHistoryAt = (orderUpdatedAt ?? shipment.status_updated_at ?? "").trim();
  const labelStatus = (shipment.label_status ?? "").toLowerCase().trim();
  const effectiveLabelURL = resolveShipmentLabelURL(orderId, shipment);
  const effectiveDaceURL = resolveShipmentDaceURL(orderId, shipment);
  const effectiveDaceCancelledURL = resolveShipmentDaceCancelledURL(orderId, shipment);
  const labelReady = !!effectiveLabelURL;
  const labelGenerating = labelStatus === "generating" || labelStatus === "pending";
  const dceStatusNorm = (shipment.dce_status ?? "").trim().toLowerCase();
  // API/Firestore often omit dce_status until first touch — treat as not_requested so seller sees the CTA.
  const dceCanStartAuthorize =
    dceStatusNorm === "" ||
    dceStatusNorm === "draft" ||
    dceStatusNorm === "not_requested" ||
    dceStatusNorm === "rejected" ||
    dceStatusNorm === "cancelled";
  const canAuthorizeDCE =
    !!isCurrentUserShipment &&
    !isLocalHandoff &&
    !orderClosed &&
    !hideCarrierLabelActions &&
    labelReady &&
    !(shipment.dce_chave_acesso ?? "").trim() &&
    dceCanStartAuthorize;
  const canCancelDCE =
    !!isCurrentUserShipment &&
    !isLocalHandoff &&
    dceStatusNorm === "authorized" &&
    !!(shipment.dce_chave_acesso ?? "").trim();
  const showDCEHintBeforeLabel =
    !!isCurrentUserShipment &&
    !isLocalHandoff &&
    !orderClosed &&
    !hideCarrierLabelActions &&
    !labelReady &&
    dceCanStartAuthorize &&
    !(shipment.dce_chave_acesso ?? "").trim();
  const dceInfoVisibleForAnyone =
    hasShipmentDCEInfo(shipment) || hasCancelledDCESnapshot(shipment);
  const showDCEBlock =
    !!isCurrentUserShipment &&
    !isLocalHandoff &&
    !orderClosed &&
    (dceInfoVisibleForAnyone || canAuthorizeDCE || canCancelDCE || showDCEHintBeforeLabel);
  const canManageLabelGeneration =
    !!showLabelLink && !!isCurrentUserShipment && !hideCarrierLabelActions && !isLocalHandoff;

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Sub-heading: Sua remessa / Remessa do vendedor / Remessa do comprador */}
      {partyLabel ? (
        <div
          className={`flex items-center gap-2 pb-3 border-b border-border ${
            isCurrentUserShipment ? "text-primary" : "text-muted-foreground"
          }`}
        >
          <Package className="w-4 h-4 shrink-0" aria-hidden />
          <span className="font-semibold text-sm uppercase tracking-wide">{partyLabel}</span>
        </div>
      ) : null}
      {!isCurrentUserShipment && !isLocalHandoff && !orderClosed ? (
        <p className="text-xs text-muted-foreground -mt-1">
          A etiqueta desta perna é solicitada pela outra parte; use o rastreio e o histórico abaixo para acompanhar.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium">
            {isLocalHandoff ? "Retirada presencial" : formatCarrierDisplayName(shipment.carrier)}
          </p>
          {isLocalHandoff ? (
            <p className="text-sm text-muted-foreground mt-1">
              Entrega combinada entre as partes, no mesmo município. Não há rastreio nem etiqueta de transportadora.
            </p>
          ) : (
            shipment.tracking_code && (
              <p className="text-sm text-muted-foreground">Código: {shipment.tracking_code}</p>
            )
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              Rastrear
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {canManageLabelGeneration && !labelReady && !labelGenerating && (
            <button
              type="button"
              disabled={labelRequestLoading}
              onClick={() => {
                void (async () => {
                  setLabelRequestLoading(true);
                  try {
                    await postRequestShipmentLabelGeneration(orderId, shipment.shipment_id);
                    await onRefreshOrder?.();
                  } catch (e) {
                    alert((e as Error)?.message ?? "Não foi possível solicitar a etiqueta.");
                  } finally {
                    setLabelRequestLoading(false);
                  }
                })();
              }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-60"
            >
              {labelRequestLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Solicitando…
                </>
              ) : (
                <>
                  Solicitar etiqueta
                  <Truck className="w-4 h-4" />
                </>
              )}
            </button>
          )}
          {showLabelLink && !hideCarrierLabelActions && labelReady && (
            <button
              type="button"
              onClick={() => {
                void openLabelURL(effectiveLabelURL, {
                  fallbackFilename: buildShipmentLabelDownloadFilename(orderId, shipmentLabelIndex1based),
                }).catch((err) => {
                  alert((err as Error)?.message ?? "Não foi possível abrir a etiqueta.");
                });
              }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              Ver e imprimir etiqueta
              <Printer className="w-4 h-4" />
            </button>
          )}
          {showLabelLink && !hideCarrierLabelActions && !labelReady && labelGenerating && (
            <span className="inline-flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-4 h-4 animate-spin" />
                Estamos gerando sua etiqueta
              </span>
            </span>
          )}
          <span className="ml-auto">
            <StatusBadge label={formatShipmentStatus(displayShipmentStatus)} status={orderClosed ? "cancelled" : undefined} />
          </span>
        </div>
      </div>

      {showDCEBlock && (
        <div className="border-t border-border pt-3 space-y-3">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 shrink-0" aria-hidden />
            Documento fiscal (DC-e)
          </p>
          {showDCEHintBeforeLabel ? (
            <p className="text-xs text-muted-foreground">
              Gere e confirme a etiqueta acima antes de solicitar a autorização da DC-e.
            </p>
          ) : null}
          {/* Vigente: autorizada com identidade ativa, ou fluxo sem snapshot cancelado ainda */}
          {dceStatusNorm === "authorized" && (shipment.dce_chave_acesso ?? "").trim() ? (
            <div className="text-sm space-y-1">
              <p className="text-xs font-medium text-muted-foreground">DC-e vigente</p>
              <p>
                <span className="text-muted-foreground">Situação: </span>
                {formatDCEStatus(shipment.dce_status)}
              </p>
              <p className="break-all">
                <span className="text-muted-foreground">Chave: </span>
                {shipment.dce_chave_acesso}
              </p>
              {(shipment.dce_protocolo ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Protocolo: </span>
                  {shipment.dce_protocolo}
                </p>
              ) : null}
              {isCurrentUserShipment && (canCancelDCE || (!!effectiveDaceURL && dceStatusNorm === "authorized")) ? (
                <div className="flex flex-wrap gap-2 pt-2">
                  {canCancelDCE && (
                    <button
                      type="button"
                      disabled={dceCancelLoading}
                      onClick={() => {
                        const j = window.prompt("Justificativa do cancelamento (mínimo 15 caracteres):");
                        if (j == null) return;
                        if (j.trim().length < 15) {
                          alert("A justificativa deve ter pelo menos 15 caracteres.");
                          return;
                        }
                        void (async () => {
                          setDceCancelLoading(true);
                          try {
                            await postCancelDCEShipment(orderId, shipment.shipment_id, j);
                            await onRefreshOrder?.();
                          } catch (e) {
                            alert((e as Error)?.message ?? "Não foi possível cancelar o DC-e.");
                          } finally {
                            setDceCancelLoading(false);
                          }
                        })();
                      }}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-destructive hover:underline disabled:opacity-60"
                    >
                      {dceCancelLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Cancelando…
                        </>
                      ) : (
                        "Cancelar DC-e"
                      )}
                    </button>
                  )}
                  {!!effectiveDaceURL && dceStatusNorm === "authorized" && (
                    <button
                      type="button"
                      onClick={() => {
                        void openLabelURL(effectiveDaceURL, {
                          kind: "dace",
                          fallbackFilename: buildDaceDownloadFilename(orderId, shipmentLabelIndex1based),
                        }).catch((err) => {
                          alert((err as Error)?.message ?? "Não foi possível abrir o DACE.");
                        });
                      }}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      Ver DACE (PDF)
                      <FileText className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ) : hasShipmentDCEInfo(shipment) && !(dceStatusNorm === "cancelled" && hasCancelledDCESnapshot(shipment)) ? (
            <div className="text-sm space-y-1">
              <p>
                <span className="text-muted-foreground">Situação: </span>
                {formatDCEStatus(shipment.dce_status)}
              </p>
              {(shipment.dce_chave_acesso ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Chave: </span>
                  {shipment.dce_chave_acesso}
                </p>
              ) : null}
              {(shipment.dce_protocolo ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Protocolo: </span>
                  {shipment.dce_protocolo}
                </p>
              ) : null}
              {shipment.dce_status !== "authorized" &&
              ((shipment.dce_last_xmotivo ?? "").trim() || (shipment.dce_last_cstat ?? "").trim()) ? (
                <p className="text-destructive text-xs">
                  {(shipment.dce_last_cstat ?? "").trim() && (
                    <span>cStat {shipment.dce_last_cstat} — </span>
                  )}
                  {(shipment.dce_last_xmotivo ?? "").trim()}
                </p>
              ) : null}
            </div>
          ) : null}
          {/* Só cancelada (sem reemissão): bloco único em estilo secundário */}
          {dceStatusNorm === "cancelled" && hasCancelledDCESnapshot(shipment) && !(shipment.dce_chave_acesso ?? "").trim() ? (
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground/80">DC-e cancelada</p>
              <p>
                <span className="text-muted-foreground">Situação: </span>
                {formatDCEStatus(shipment.dce_status)}
              </p>
              {(shipment.dce_cancelled_chave_acesso ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Chave: </span>
                  {shipment.dce_cancelled_chave_acesso}
                </p>
              ) : null}
              {(shipment.dce_cancelled_protocolo ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Protocolo: </span>
                  {shipment.dce_cancelled_protocolo}
                </p>
              ) : null}
              {((shipment.dce_cancelled_last_xmotivo ?? "").trim() || (shipment.dce_cancelled_last_cstat ?? "").trim()) ? (
                <p className="text-destructive text-xs">
                  {(shipment.dce_cancelled_last_cstat ?? "").trim() && (
                    <span>cStat {shipment.dce_cancelled_last_cstat} — </span>
                  )}
                  {(shipment.dce_cancelled_last_xmotivo ?? "").trim()}
                </p>
              ) : null}
            </div>
          ) : null}
          {/* Após reemissão: snapshot da última cancelada abaixo da vigente */}
          {dceStatusNorm === "authorized" && (shipment.dce_chave_acesso ?? "").trim() && hasCancelledDCESnapshot(shipment) ? (
            <div className="text-xs text-muted-foreground space-y-1 pt-1 border-t border-border/60">
              <p className="font-medium text-foreground/80">DC-e cancelada (anterior)</p>
              {(shipment.dce_cancelled_chave_acesso ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Chave: </span>
                  {shipment.dce_cancelled_chave_acesso}
                </p>
              ) : null}
              {(shipment.dce_cancelled_protocolo ?? "").trim() ? (
                <p className="break-all">
                  <span className="text-muted-foreground">Protocolo: </span>
                  {shipment.dce_cancelled_protocolo}
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {canAuthorizeDCE && (
              <button
                type="button"
                disabled={dceAuthLoading}
                onClick={() => {
                  void (async () => {
                    setDceAuthLoading(true);
                    try {
                      await postAuthorizeDCEShipment(orderId, shipment.shipment_id);
                      await onRefreshOrder?.();
                    } catch (e) {
                      alert((e as Error)?.message ?? "Não foi possível autorizar o DC-e.");
                    } finally {
                      setDceAuthLoading(false);
                    }
                  })();
                }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-60"
              >
                {dceAuthLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Autorizando…
                  </>
                ) : (
                  <>
                    {dceStatusNorm === "rejected"
                      ? "Tentar autorizar DC-e novamente"
                      : dceStatusNorm === "cancelled"
                        ? "Emitir nova DC-e"
                        : "Solicitar autorização DC-e"}
                    <FileText className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
            {!!effectiveDaceCancelledURL && isCurrentUserShipment && hasCancelledDCESnapshot(shipment) && (
              <button
                type="button"
                onClick={() => {
                  void openLabelURL(effectiveDaceCancelledURL, {
                    kind: "dace",
                    fallbackFilename: buildDaceCancelledDownloadFilename(orderId, shipmentLabelIndex1based),
                  }).catch((err) => {
                    alert((err as Error)?.message ?? "Não foi possível abrir o DACE cancelado.");
                  });
                }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:underline"
              >
                Ver DACE (PDF) - Cancelado
                <FileText className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Timeline: rastreio (status_history). Local handoff: sem etiqueta real — ocultar histórico (status já no topo do pedido). */}
      {!isLocalHandoff && (orderClosed || sortedHistory.length > 0) && (
        <div className="border-t border-border pt-3 min-w-0 overflow-x-hidden">
          <p className="text-xs font-medium text-muted-foreground mb-2">Histórico</p>
          <ul className="space-y-2 min-w-0">
            {sortedHistory.map((entry, i) => (
              <li
                key={i}
                className="grid grid-cols-[auto_minmax(8px,1fr)_minmax(0,1fr)] gap-x-2 items-center text-sm min-w-0"
              >
                <span className="text-muted-foreground whitespace-nowrap tabular-nums shrink-0">
                  {formatDate(entry.at)}
                </span>
                <span
                  className="min-h-px min-w-2 border-b border-dashed border-border self-center"
                  aria-hidden
                />
                <span className="min-w-0 text-right break-words hyphens-auto">
                  {formatShipmentStatus(entry.status)}
                </span>
              </li>
            ))}
            {orderClosed && (
              <li className="grid grid-cols-[auto_minmax(8px,1fr)_minmax(0,1fr)] gap-x-2 items-center text-sm min-w-0">
                <span className="text-muted-foreground whitespace-nowrap tabular-nums shrink-0">
                  {shipmentClosedHistoryAt ? formatDate(shipmentClosedHistoryAt) : "—"}
                </span>
                <span
                  className="min-h-px min-w-2 border-b border-dashed border-border self-center"
                  aria-hidden
                />
                <span className="min-w-0 text-right break-words hyphens-auto text-destructive font-medium">
                  Pedido/Etiqueta cancelada
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
